import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import dayjs from "dayjs";

Font.register({
  family: "Manrope",
  fonts: [
    { src: "/fonts/Manrope-Regular.ttf" },
    { src: "/fonts/Manrope-Bold.ttf", fontWeight: "bold" },
  ],
});

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 9,
    fontFamily: "Manrope",
    color: "#333",
  },
  headerSpace: {
    width: "100%",
    height: 70,
    marginBottom: 15,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  header_image: {
    width: "100%",
    height: "100%",
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
    textTransform: "uppercase",
    marginBottom: 15,
  },
  period: {
    fontSize: 9,
    textAlign: "center",
    marginBottom: 20,
    color: "#666",
  },
  empSection: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#000",
    marginBottom: 15,
  },
  empInfoBox: {
    flex: 1,
    padding: 6,
    borderRightWidth: 1,
    borderRightColor: "#000",
  },
  empInfoBoxLast: {
    width: 120,
    padding: 6,
  },
  label: {
    fontSize: 7,
    color: "#666",
    textTransform: "uppercase",
    marginBottom: 2,
    fontWeight: "bold",
  },
  value: {
    fontSize: 9,
    fontWeight: "bold",
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: "bold",
    backgroundColor: "#2663EB",
    color: "#fff",
    padding: 5,
    textAlign: "center",
    marginBottom: 0,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f2f2f2",
    borderWidth: 1,
    borderColor: "#000",
    borderTopWidth: 0,
  },
  tableRow: {
    flexDirection: "row",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    minHeight: 20,
    alignItems: "center",
  },
  cellHeader: {
    flex: 1,
    padding: 4,
    fontSize: 7,
    fontWeight: "bold",
    textAlign: "center",
    borderRightWidth: 1,
    borderColor: "#000",
  },
  cellDesc: {
    flex: 2,
    padding: 4,
    fontSize: 8,
    borderRightWidth: 1,
    borderColor: "#000",
  },
  cellAmount: {
    flex: 1,
    padding: 4,
    fontSize: 8,
    textAlign: "right",
    fontWeight: "bold",
    borderRightWidth: 1,
    borderColor: "#000",
  },
  cellAmountLast: {
    flex: 1,
    padding: 4,
    fontSize: 8,
    textAlign: "right",
    fontWeight: "bold",
  },
  totalRow: {
    flexDirection: "row",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    minHeight: 22,
    alignItems: "center",
    backgroundColor: "#d4edda",
  },
  totalLabel: {
    flex: 2,
    padding: 4,
    fontSize: 9,
    fontWeight: "bold",
    textAlign: "right",
    borderRightWidth: 1,
    borderColor: "#000",
  },
  totalAmount: {
    flex: 1,
    padding: 4,
    fontSize: 9,
    fontWeight: "bold",
    textAlign: "right",
    color: "red",
  },
  footer: {
    position: "absolute",
    bottom: 15,
    left: 30,
    right: 30,
    textAlign: "center",
    fontSize: 7,
    color: "grey",
  },
});

const formatCurrency = (amt) =>
  Number(amt || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

const PaySlipDocument = ({ data, startDate, endDate, headerImage, footerImage }) => {
  const earnings = [
    { label: "Basic Pay", value: data?.BASIC_PAY },
    { label: "Allowances", value: data?.ALLOWANCES },
    { label: "Increments", value: data?.INCREMENTS },
    { label: "Overtime (NR)", value: data?.NR_HRS ? data.NR_HRS : "-" },
    { label: "Overtime (OT)", value: data?.OT_HRS ? data.OT_HRS : "-" },
    { label: "Overtime (OTS)", value: data?.OTS_HRS ? data.OTS_HRS : "-" },
    { label: "Other Additions", value: data?.OTH_ADDITIONS },
  ];

  const deductions = [
    { label: "Absent Days", value: data?.ABS_DAYS },
    { label: "Deductions", value: data?.DEDUCTIONS },
    { label: "Loans & Advances", value: data?.LOANS_AND_ADV },
  ];

  const totalEarnings =
    (parseFloat(data?.BASIC_PAY) || 0) +
    (parseFloat(data?.ALLOWANCES) || 0) +
    (parseFloat(data?.INCREMENTS) || 0) +
    (parseFloat(data?.OTH_ADDITIONS) || 0);

  const totalDeductions =
    (parseFloat(data?.DEDUCTIONS) || 0) + (parseFloat(data?.LOANS_AND_ADV) || 0);

  const netPay = totalEarnings - totalDeductions;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {headerImage && (
          <View style={styles.headerSpace} fixed>
            <Image src={headerImage} style={styles.header_image} />
          </View>
        )}

        <Text style={styles.title}>Payslip</Text>
        <Text style={styles.period}>
          Period: {dayjs(startDate).format("DD-MMM-YYYY")} -{" "}
          {dayjs(endDate).format("DD-MMM-YYYY")}
        </Text>

        <View style={styles.empSection}>
          <View style={styles.empInfoBox}>
            <Text style={styles.label}>Employee ID</Text>
            <Text style={styles.value}>{data?.EMP_ID || "-"}</Text>
          </View>
          <View style={styles.empInfoBox}>
            <Text style={styles.label}>Name</Text>
            <Text style={styles.value}>{data?.NAME || "-"}</Text>
          </View>
          <View style={styles.empInfoBox}>
            <Text style={styles.label}>Department</Text>
            <Text style={styles.value}>{data?.DEPARTMENT || "-"}</Text>
          </View>
          <View style={styles.empInfoBoxLast}>
            <Text style={styles.label}>Job Title</Text>
            <Text style={styles.value}>{data?.JOB_TITLE || "-"}</Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Earnings</Text>
        <View style={styles.tableHeader}>
          <Text style={[styles.cellHeader, { flex: 2 }]}>Description</Text>
          <Text style={[styles.cellHeader, { flex: 1, borderRightWidth: 0 }]}>Amount</Text>
        </View>
        {earnings.map((item, i) => (
          <View key={i} style={styles.tableRow}>
            <Text style={styles.cellDesc}>{item.label}</Text>
            <Text style={styles.cellAmountLast}>
              {item.value ? formatCurrency(item.value) : "-"}
            </Text>
          </View>
        ))}
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>Total Earnings</Text>
          <Text style={styles.totalAmount}>{formatCurrency(totalEarnings)}</Text>
        </View>

        <View style={{ height: 10 }} />

        <Text style={styles.sectionTitle}>Deductions</Text>
        <View style={styles.tableHeader}>
          <Text style={[styles.cellHeader, { flex: 2 }]}>Description</Text>
          <Text style={[styles.cellHeader, { flex: 1, borderRightWidth: 0 }]}>Amount</Text>
        </View>
        {deductions.map((item, i) => (
          <View key={i} style={styles.tableRow}>
            <Text style={styles.cellDesc}>{item.label}</Text>
            <Text style={styles.cellAmountLast}>
              {item.value ? formatCurrency(item.value) : "-"}
            </Text>
          </View>
        ))}
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>Total Deductions</Text>
          <Text style={styles.totalAmount}>{formatCurrency(totalDeductions)}</Text>
        </View>

        <View style={{ height: 10 }} />

        <View
          style={{
            flexDirection: "row",
            borderWidth: 1,
            borderColor: "#000",
            minHeight: 25,
            alignItems: "center",
            backgroundColor: "#2663EB",
          }}
        >
          <Text
            style={{
              flex: 2,
              padding: 5,
              fontSize: 11,
              fontWeight: "bold",
              color: "#fff",
              textAlign: "right",
              borderRightWidth: 1,
              borderColor: "#fff",
            }}
          >
            Net Pay
          </Text>
          <Text
            style={{
              flex: 1,
              padding: 5,
              fontSize: 12,
              fontWeight: "bold",
              color: "#fff",
              textAlign: "right",
            }}
          >
            {formatCurrency(netPay)}
          </Text>
        </View>

        <View style={styles.footer} fixed>
          {footerImage && <Image src={footerImage} style={styles.header_image}  />}
        </View>
      </Page>
    </Document>
  );
};

export default PaySlipDocument;
